import {mapRunningTimeDto} from '@domain/shared'
import {
  EquipmentData,
  Iso8601,
  RunningTime,
  EquipmentRunningTimesDto,
  DateRange
} from '@hconnect/common/types'
import {Box, Grid} from '@mui/material'
import {get, last} from 'lodash'
import moment from 'moment'
import React, {ReactNode, useCallback, useMemo} from 'react'

import {mapEquipmentRunningTimes} from '../mappers'
import {RunningTimesStoppageCodesByType} from '../types'
import {getRunningTimesWithGap} from '../utils'

import {EquipmentRunningTimesBarChart, StackedBarChart} from './charts'
import {OperatingHoursLegendList} from './OperatingHoursLegendList'

type Props = {
  runningTimePerformances?: EquipmentRunningTimesDto[] | undefined
  from: Iso8601
  to: Iso8601
  timezone: string
  stoppageCodesByType: RunningTimesStoppageCodesByType
  onEquipmentClick?: (equipmentId: string) => void
  oneColumnView?: boolean
  hideLegend?: boolean
  renderRunningTimeTooltipFooter?: (rt: RunningTime) => ReactNode
}

export const OperationHoursList: React.FC<Props> = React.memo(
  ({
    runningTimePerformances,
    from,
    to,
    timezone,
    stoppageCodesByType,
    onEquipmentClick,
    oneColumnView,
    hideLegend = false,
    renderRunningTimeTooltipFooter
  }) => {
    const timeRange = useMemo<DateRange>(
      () => ({
        startDate: moment.utc(from).tz(timezone),
        endDate: moment.utc(to).tz(timezone)
      }),
      [from, to, timezone]
    )

    const getStackedBarChartComponent = useCallback(
      (equipment: EquipmentData, runningTimes: RunningTime[]) => {
        return (
          <StackedBarChart
            timeRange={timeRange}
            timezone={timezone}
            data={getRunningTimesWithGap(runningTimes.map(mapRunningTimeDto), timeRange, timezone)}
            equipment={equipment}
            stoppageCodesByType={stoppageCodesByType}
            renderTooltipFooter={renderRunningTimeTooltipFooter}
          />
        )
      },
      [timeRange, timezone, stoppageCodesByType, renderRunningTimeTooltipFooter]
    )

    return (
      <>
        {!hideLegend && <OperatingHoursLegendList />}

        {oneColumnView ? (
          runningTimePerformances?.map((equipmentPerformanceData) => {
            const activeStatus = get(last(equipmentPerformanceData.runningTimes), 'runningTimeType')

            return (
              <Box sx={{width: '100%'}} key={equipmentPerformanceData.equipment.id}>
                <EquipmentRunningTimesBarChart
                  runningTimePerformance={
                    mapEquipmentRunningTimes([equipmentPerformanceData], timezone)[0]
                  }
                  equipment={equipmentPerformanceData.equipment}
                  getStackedBarChartComponent={getStackedBarChartComponent}
                  activeStatus={activeStatus}
                  onClick={() => onEquipmentClick?.(equipmentPerformanceData.equipment.id)}
                  sx={{mb: 1}}
                />
              </Box>
            )
          })
        ) : (
          <Grid rowSpacing={1} columnSpacing={3} container overflow={'hidden'}>
            {runningTimePerformances?.map((equipmentPerformanceData) => {
              const activeStatus = get(
                last(equipmentPerformanceData.runningTimes),
                'runningTimeType'
              )

              return (
                <Grid item xs={6} key={equipmentPerformanceData.equipment.id}>
                  <EquipmentRunningTimesBarChart
                    runningTimePerformance={
                      mapEquipmentRunningTimes([equipmentPerformanceData], timezone)[0]
                    }
                    equipment={equipmentPerformanceData.equipment}
                    getStackedBarChartComponent={getStackedBarChartComponent}
                    activeStatus={activeStatus}
                    onClick={() => onEquipmentClick?.(equipmentPerformanceData.equipment.id)}
                  />
                </Grid>
              )
            })}
          </Grid>
        )}
      </>
    )
  }
)
